import * as React from 'react';
import _ from 'lodash';
import Input from '../Input/Input';

import './slider.scss';

type Props = {
    showInput?: boolean;
    value?: number;
    unit?: string;
    min?: number;
    max?: number;
    step?: number;
    disabled?: boolean;
    displayValues?: number[];
    onChange?: (value: number) => void;
    id?: string;
    'aria-labelledby'?: React.InputHTMLAttributes<'input'>['aria-labelledby'];
    'aria-describedby'?: React.InputHTMLAttributes<'input'>['aria-describedby'];
};

const getBgColor = (value: number, min: number, max: number) => {
    const percentValue = ((value - min) / (max - min)) * 100;
    return `linear-gradient(to right, currentColor 0%, currentColor ${percentValue}%, transparent ${percentValue}%, transparent 100%)`;
};

const Slider = React.forwardRef<HTMLDivElement, Props>((props, ref) => {
    const { min = 0, max = 100, step = 1, value, displayValues } = props;

    const defaultValue = React.useMemo(() => Math.ceil((max - min) / 2), [max, min]);
    const range = React.useMemo(() => [..._.range(min, max, step), max], [min, max, step]);
    const [currentValue, setCurrentValue] = React.useState(props.value ?? defaultValue);
    const displayValue = displayValues?.[currentValue]?.toString() ?? currentValue.toString();
    const [currentTextValue, setCurrentTextValue] = React.useState(displayValue);
    const [isFocusedTextInput, setFocusedTextInput] = React.useState(false);

    const valueBeforeEditText = React.useRef(min);
    const shouldSave = React.useRef(true);
    const hasBeenEdited = React.useRef(false);

    React.useEffect(() => {
        setCurrentValue(value ?? defaultValue);
    }, [value, defaultValue]);

    React.useEffect(() => {
        setCurrentTextValue(displayValue);
    }, [displayValue]);

    const onChangeHandler = (newValue: number | undefined) => {
        if (!newValue || Number.isNaN(newValue)) {
            newValue = min!;
        } else if (newValue < min!) {
            newValue = min;
        } else if (newValue > max!) {
            newValue = max;
        }

        if (!range.includes(newValue)) {
            const maxStepIndex = range.findIndex((curRangeValue) => curRangeValue > newValue!);
            const maxStep = range[maxStepIndex];
            const minStep = range[maxStepIndex - 1]!; // step 5; 43 -> 45; 42 -> 40
            newValue = (newValue - minStep) / step > 0.5 ? maxStep : minStep;
        }

        setCurrentValue(newValue!);
        setCurrentTextValue(displayValue);

        if (newValue !== value) {
            props.onChange?.(newValue ?? min);
        }
    };

    const isValueDefined = typeof value !== 'undefined' || hasBeenEdited.current;
    const textInputUnitValue = isValueDefined ? `${displayValue}${props.unit || ''}` : '';

    return (
        <div className="slider" ref={ref}>
            {props.showInput && (
                <Input
                    aria-describedby={props['aria-describedby']}
                    aria-labelledby={props['aria-labelledby']}
                    type="text"
                    value={isFocusedTextInput ? currentTextValue : textInputUnitValue}
                    className="slider__text-input"
                    disabled={props.disabled}
                    onChange={(e) => {
                        setCurrentTextValue(e.target.value);
                        const numberValue = parseInt(e.target.value, 10);
                        if (!Number.isNaN(numberValue)) {
                            let realNumber: number | undefined = numberValue;
                            if (displayValues) {
                                realNumber = displayValues.indexOf(numberValue);
                                if (realNumber === -1) {
                                    realNumber = undefined;
                                }
                            }
                            if (typeof realNumber === 'number') {
                                setCurrentValue(realNumber);
                            }
                        }
                    }}
                    onFocus={() => {
                        valueBeforeEditText.current = currentValue;
                        setFocusedTextInput(true);
                    }}
                    onKeyDown={(e) => {
                        if (['Enter', 'Escape'].includes(e.key)) {
                            const input = e.target as HTMLInputElement;
                            if (e.key === 'Escape') {
                                setCurrentValue(valueBeforeEditText.current);
                                shouldSave.current = false;
                            }
                            input.blur();
                        }
                    }}
                    onBlur={() => {
                        if (shouldSave.current && currentValue !== valueBeforeEditText.current) {
                            onChangeHandler(currentValue);
                        } else {
                            setCurrentTextValue(displayValue);
                        }
                        shouldSave.current = true;
                        setFocusedTextInput(false);
                    }}
                />
            )}
            <div className="slider__input-container">
                <input
                    id={props.id}
                    aria-describedby={props['aria-describedby']}
                    aria-labelledby={props['aria-labelledby']}
                    type="range"
                    value={currentValue}
                    className="slider__input"
                    disabled={props.disabled}
                    min={min}
                    max={max}
                    step={step}
                    onChange={(e) => {
                        hasBeenEdited.current = true;
                        setCurrentValue(e.target.valueAsNumber);
                    }}
                    onMouseUp={() => onChangeHandler(currentValue)}
                    onTouchEnd={() => onChangeHandler(currentValue)}
                    onInput={(e) => {
                        const input = e.target as HTMLInputElement;
                        input.style.setProperty('background', getBgColor(input.valueAsNumber, min, max), 'important');
                    }}
                    ref={(e) => {
                        e?.style.setProperty('background', getBgColor(currentValue, min, max), 'important');
                    }}
                />
            </div>
        </div>
    );
});
Slider.displayName = 'Slider';

export default Slider;
